import { useEffect, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { BulkFillReport, SelectionActionsBarProps, SpreadsheetSelectionAction } from './SelectionActionsBar.props';
import { classes, Root } from './SelectionActionsBar.styles';

const FILL_REPORT_VISIBLE_MS = 3000;

const ActionButton = ({
  action,
  snapshot,
  onClear,
}: { action: SpreadsheetSelectionAction } & Pick<SelectionActionsBarProps, 'snapshot' | 'onClear'>) => {
  const button = (
    <Button
      data-testid={`selection-action-${action.key}`}
      variant="outlined"
      size="small"
      color={action.tone === 'danger' ? 'error' : 'primary'}
      disabled={action.disabled}
      startIcon={action.icon}
      onClick={() => action.onClick(snapshot, onClear)}>
      {action.label}
    </Button>
  );

  // The span keeps the tooltip working when the button is disabled.
  return action.tooltip ? (
    <Tooltip title={action.tooltip}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

const SelectionActionsBar = ({ snapshot, actions, onClear, fillReport, className }: SelectionActionsBarProps) => {
  const [expiredReport, setExpiredReport] = useState<BulkFillReport | null>(null);

  useEffect(() => {
    if (!fillReport) {
      return undefined;
    }
    const timer = setTimeout(() => setExpiredReport(fillReport), FILL_REPORT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [fillReport]);

  if (actions.length === 0) {
    return null;
  }

  const showReport = fillReport && fillReport !== expiredReport;

  return (
    <Root
      className={clsx(classes.root, className)}
      data-testid="selection-actions-bar"
      role="toolbar"
      aria-label="Selection actions">
      {snapshot && (
        <>
          <span className={classes.summary}>
            {snapshot.cells.length} cells / {snapshot.rows.length} rows / {snapshot.cols.length} cols
          </span>
          <span className={classes.divider}>|</span>
        </>
      )}
      {actions.map(action => (
        <ActionButton key={action.key} action={action} snapshot={snapshot} onClear={onClear} />
      ))}
      {showReport && (
        <span className={classes.report} data-testid="selection-fill-report">
          Filled {fillReport.filled} of {fillReport.filled + fillReport.skipped}
        </span>
      )}
      <Button data-testid="selection-actions-clear" variant="text" size="small" onClick={onClear}>
        Clear
      </Button>
    </Root>
  );
};

export default SelectionActionsBar;
